import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { WSRegistration } from "../configs/WebService";
import Popup from "../features/EventOrganiser/Popup";
import TermsAndConditions from "./TermsAndConditions";
import StudentRegistrationForm from "./StudentRegistrationForm";
import IndividualRegistrationForm from "./IndividualRegistrationForm";
import ForOthersRegistrationForm from "./ForOthersRegistrationForm";
import { useDispatch, useSelector } from "react-redux";
import LoaderOverlay from "./LoaderOverlay";
import { motion } from "framer-motion";
import AudioToggleButton from "../components/AudioToggleButton";
import {
  getBotAccessList,
  sendFailedEmail,
  sendWelcomeEmail,
  sendWelcomeSMS,
  sendWelcomeTemplate,
} from "../features/student/redux/studentThunk";
import { clearGoogleAndMobile } from "../features/QuestionPaper/createpaper/components/registration/redux/registerSlice";
import { shouldSendFailedEmail } from "../features/student/redux/utils";
import { setAppdAiGuest } from "../utils/appdAiHelpers";
import { trackMetaPixelRegistration } from "../utils/metaPixel";
import { fireRegistrationWebhook } from "../utils/webhook";
import RegistrationStepper from "../components/RegistrationStepper";

const LOADER_STEPS = [
  { text: "Setting up your account...", icon: "🔧", duration: 3000 },
  { text: "Configuring your dashboard...", icon: "📊", duration: 4000 },
  { text: "Almost there, hang tight...", icon: "⏳", duration: 5000 },
  { text: "Just a few more seconds...", icon: "🚀", duration: 0 },
];

const RegistrationLoader = () => {
  const [stepIndex, setStepIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // Progress bar animation
    const progressInterval = setInterval(() => {
      setProgress((prev) => (prev >= 95 ? 95 : prev + 1));
    }, 150);

    // Step messages
    let totalDelay = 0;
    const timers = LOADER_STEPS.map((step, i) => {
      if (i === 0) return null;
      totalDelay += LOADER_STEPS[i - 1].duration;
      return setTimeout(() => setStepIndex(i), totalDelay);
    }).filter(Boolean);

    return () => {
      clearInterval(progressInterval);
      timers.forEach((t) => clearTimeout(t));
    };
  }, []);

  const currentStep = LOADER_STEPS[stepIndex];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "50px 20px",
        gap: "20px",
      }}
    >
      <div
        style={{
          width: "64px",
          height: "64px",
          borderRadius: "50%",
          background: "linear-gradient(135deg, #667eea, #764ba2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "28px",
          animation: "pulse 1.5s ease-in-out infinite",
        }}
      >
        {currentStep.icon}
      </div>

      <p
        style={{
          fontSize: "17px",
          fontWeight: 700,
          color: "#4a5568",
          textAlign: "center",
          transition: "opacity 0.3s ease",
        }}
      >
        {currentStep.text}
      </p>

      {/* Progress bar */}
      <div
        style={{
          width: "80%",
          maxWidth: "280px",
          height: "8px",
          background: "#e2e8f0",
          borderRadius: "10px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${progress}%`,
            height: "100%",
            background: "linear-gradient(90deg, #667eea, #764ba2, #f093fb)",
            borderRadius: "10px",
            transition: "width 0.3s ease",
          }}
        />
      </div>

      <p
        style={{
          fontSize: "12px",
          color: "#a0aec0",
          fontWeight: 600,
        }}
      >
        Please don't close or refresh the page
      </p>

      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(118, 75, 162, 0.4); }
          50% { transform: scale(1.08); box-shadow: 0 0 20px 5px rgba(118, 75, 162, 0.2); }
        }
      `}</style>
    </div>
  );
};

const SignupPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, error, botAccess } = useSelector((state) => state.student);
  const [userType, setUserType] = useState("");
  const [searchParams] = useSearchParams();
  const [loader, setLoader] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const { googleEmail, mobile, adminName } = useSelector(
    (state) => state.register,
  );

  const { state } = useLocation();
  // const registerType = state?.register_type;
  const registerType = state?.register_type
    ? state?.register_type
    : "direct_signup_page";

  const audioRef = useRef(null);
  const audioPausedAt4s = useRef(false);
  const audioScheduleTimers = useRef([]);
  const audioBTimerRef = useRef(null);
  const audioBRef = useRef(null);

  const stopAudio = () => {
    // Clear any scheduled play/pause timers
    audioScheduleTimers.current.forEach((t) => clearTimeout(t));
    audioScheduleTimers.current = [];
    // Clear audioB delay timer
    if (audioBTimerRef.current) {
      clearTimeout(audioBTimerRef.current);
      audioBTimerRef.current = null;
    }
    // Stop current audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    // Stop audioB as well
    if (audioBRef.current) {
      audioBRef.current.pause();
      audioBRef.current.currentTime = 0;
    }
  };

  const resumeAudio = () => {
    if (audioPausedAt4s.current && audioRef.current) {
      audioPausedAt4s.current = false;
      const audio = audioRef.current;

      // Schedule: play 4-11, pause 5s, play 11-16, pause 5s, play 16-18, pause 5s, continue from 18
      const schedule = [
        { action: "play", time: 4, delay: 0 },          // play 4 → 11.5
        { action: "pause", delay: 7500 },              // pause at 11.5s mark
        { action: "play", time: 11.5, delay: 12500 },  // play 11.5 → 16.5
        { action: "pause", delay: 17500 },             // pause at 16.5s mark
        { action: "play", time: 16.5, delay: 22500 },  // play 16.5 → 18.5
        { action: "pause", delay: 24500 },             // pause at 18.5s mark
        { action: "play", time: 18.5, delay: 29500 },  // play 18.5 → 20.5
        { action: "pause", delay: 31500 },             // pause at 20.5s mark
        { action: "play", time: 20.5, delay: 36500 },  // play 20.5 → 25
        { action: "pause", delay: 41000 },             // pause at 25s mark
        { action: "play", time: 25, delay: 46000 },    // play 25 → end of audioA
      ];

      schedule.forEach((step) => {
        const timer = setTimeout(() => {
          if (step.action === "play") {
            audio.currentTime = step.time;
            audio.play().catch(() => {});
          } else {
            audio.pause();
          }
        }, step.delay);
        audioScheduleTimers.current.push(timer);
      });
    }
  };

  // useEffect(() => {
  //   setAppdAiGuest("registration2");
  // }, []);

  // Autoplay first 4s of audioA, resume remaining on user type selection
  useEffect(() => {
    const audioA = new Audio("/audio/Registration page/Script 3a — mdm_reg_open_v1.mp3");
    const audioB = new Audio("/audio/Registration page/Script 3b — mdm_inactive_v1.mp3");
    audioRef.current = audioA;
    audioBRef.current = audioB;

    const handleAudioAEnded = () => {
      audioBTimerRef.current = setTimeout(() => {
        audioRef.current = audioB;
        audioB.play().catch(() => {});
      }, 5000);
    };

    audioA.addEventListener("ended", handleAudioAEnded);

    // Autoplay and pause after 3 seconds
    audioA.play().catch(() => {});
    const pauseTimer = setTimeout(() => {
      if (!audioA.paused && !audioA.ended) {
        audioA.pause();
        audioPausedAt4s.current = true;
      }
    }, 3500);

    return () => {
      clearTimeout(pauseTimer);
      if (audioBTimerRef.current) clearTimeout(audioBTimerRef.current);
      audioScheduleTimers.current.forEach((t) => clearTimeout(t));
      audioScheduleTimers.current = [];
      audioA.pause();
      audioA.currentTime = 0;
      audioA.removeEventListener("ended", handleAudioAEnded);
      audioB.pause();
      audioB.currentTime = 0;
      audioRef.current = null;
      audioBRef.current = null;
      audioBTimerRef.current = null;
      audioPausedAt4s.current = false;
    };
  }, []);

  useEffect(() => {
    if (googleEmail) {
      setFormData((prev) => ({ ...prev, email: googleEmail }));
    }
  }, [googleEmail]);
  useEffect(() => {
    if (mobile) {
      setFormData((prev) => ({ ...prev, contactNumber: mobile }));
    }
  }, [mobile]);
  useEffect(() => {
    if (adminName) {
      setFormData((prev) => ({ ...prev, adminName: adminName }));
    }
  }, [adminName]);

  const [formData, setFormData] = useState({
    schoolName: "",
    email: googleEmail || "",
    contactNumber: mobile || "",
    adminName: "",
    password: "",
    confirmPassword: "",
    address: "",
    schoolType: "",
    city: "",
    designation: "",
    referralCode: "",
  });

  const location = useLocation();
  const isDemoUser = true;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    dispatch(getBotAccessList());
  }, [dispatch]);

  useEffect(() => {
    const refCode = searchParams.get("ref");

    if (refCode && /^[a-zA-Z0-9]{8}$/.test(refCode)) {
      setFormData((prev) => ({
        ...prev,
        referralCode: refCode,
      }));
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    stopAudio();

    if (!/^\d{10}$/.test(formData.contactNumber)) {
      setPhoneError("Phone number must be exactly 10 digits");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      alert("Password and Confirm Password do not match!");
      return;
    }
    setLoader(true);
    const payload = {
      account_type: userType,
      ...formData,
      role: isDemoUser ? "trial" : "super admin",
      city: formData.city || "",
      designation: formData.designation || "",
      registerType: registerType || "",
      referralCode: formData.referralCode || null,
    };

    try {
      const response = await fetch(WSRegistration, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (response.ok) {
        // trackMetaPixelRegistration();

        // Fire-and-forget webhook for registration
        fireRegistrationWebhook({
          phone_number: formData.contactNumber,
          email: formData.email,
          user_type: userType,
        });

        // Notifications moved to OTP verification page (sent after mobile verification)
        // const notifications = [];
        // if (botAccess?.isWhatsapp === 1) {
        //   notifications.push(dispatch(sendWelcomeTemplate(payload)).unwrap());
        // }
        // if (botAccess?.isEmailSuccessful === 1) {
        //   notifications.push(dispatch(sendWelcomeEmail(payload)).unwrap());
        // }
        // if (botAccess?.isSMS === 1) {
        //   notifications.push(dispatch(sendWelcomeSMS(payload)).unwrap());
        // }
        // if (notifications.length > 0) {
        //   try {
        //     await Promise.all(notifications);
        //   } catch (notifyError) {
        //     console.error("Notification sending error:", notifyError);
        //   }
        // }

        alert(
          "OTP has been sent to your email. Please verify to complete registration.",
        );
        dispatch(clearGoogleAndMobile());
        setFormData({
          schoolName: "",
          email: "",
          contactNumber: "",
          adminName: "",
          password: "",
          confirmPassword: "",
          address: "",
          schoolType: "",
          referralCode: "",
        });
        navigate("/otp", {
          state: {
            formData: payload,
            account_type: userType,
            isWhatsapp: botAccess?.isWhatsapp,
            isEmailSuccessful: botAccess?.isEmailSuccessful,
            isSMS: botAccess?.isSMS,
          },
        });
      } else {
        alert("Error: " + data.error);
      }
    } catch (error) {
      // alert("Network error! Please try again.");
      // console.error(error);
      try {
        if (shouldSendFailedEmail(payload)) {
          await dispatch(sendFailedEmail(payload)).unwrap();
        }
      } catch (emailError) {
        console.error("Failed email sending error:", emailError);
      }
      alert("Network error! Please try again.");
      console.error(error);
    } finally {
      setLoader(false);
    }
  };

  const userTypes = [
    { value: "individual", label: "Teacher", emoji: "👤", color: "#ec4899" },
    { value: "school", label: "School", emoji: "🏫", color: "#3b82f6" },
    { value: "student", label: "Student", emoji: "🎓", color: "#10b981" },
    { value: "coaching", label: "Coaching", emoji: "📚", color: "#8b5cf6" },
    { value: "other", label: "Other", emoji: "👥", color: "#f59e0b" },
  ];

  const activeStepIndex = loader ? 1 : 0;

  return (
    <>
      <AudioToggleButton audioRef={audioRef} />
      <style>{`
        /* SignupPage Styles - Simple & Colorful */

.signup-page-container {
  min-height: 100vh;
  display: flex;
  justify-content: flex-start;
  align-items: stretch;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
  position: relative;
  overflow: hidden;
  font-family: 'Nunito', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}
  #root .signup-main-column .signup-notice{
 width:700px;
}

.signup-layout {
  width: 100%;
  display: flex;
  align-items: stretch;
  justify-content: center;
  gap: 28px;
  position: relative;
  z-index: 1;
}

.signup-sidebar {
  width: 250px;
  flex-shrink: 0;
  display: flex;
}

.signup-main-column {
  width: 100%;
  max-width: 520px;
  margin:40px 0px;
}

.signup-notice {
  width: 100%;
  text-align: center;
  margin-bottom: 16px;
  padding: 18px 24px;
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
}

.signup-card {
  background: #ffffff;
  border-radius: 32px;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.2);
  width: 100%;
  position: relative;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.signup-card-inner {
  padding: 20px 35px;
  overflow-y: auto;
  flex: 1;
}

/* Subtle scrollbar for card inner */
.signup-card-inner::-webkit-scrollbar {
  width: 5px;
}

.signup-card-inner::-webkit-scrollbar-track {
  background: transparent;
  margin: 20px 0;
}

.signup-card-inner::-webkit-scrollbar-thumb {
  background: #c0cfe0;
  border-radius: 10px;
}

.signup-card-inner::-webkit-scrollbar-thumb:hover {
  background: #a0b4cc;
}

.signup-card-inner {
  scrollbar-width: thin;
  scrollbar-color: #c0cfe0 transparent;
}

.signup-header {
  text-align: center;
  margin-bottom: 10px;
}

.header-badge {
  display: inline-block;
  background: linear-gradient(135deg, #ff6b6b, #feca57);
  color: white;
  padding: 8px 20px;
  border-radius: 25px;
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 15px;
  box-shadow: 0 4px 15px rgba(255, 107, 107, 0.4);
}

.signup-title {
  font-size: 26px;
  font-weight: 900;
  color: #2d3748;
  margin-bottom: 10px;
}

.gradient-text {
  background: linear-gradient(135deg, #667eea, #764ba2, #f093fb);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.signup-subtitle {
  font-size: 16px;
  color: #718096;
  font-weight: 600;
}

.user-type-section {
  margin-bottom: 25px;
}

.section-label {
  font-size: 14px;
  font-weight: 700;
  color: #4a5568;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-type-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
  background: #f7fafc;
  padding: 10px;
  border-radius: 20px;
  border: 2px solid #e2e8f0;
}

.user-type-option {
  position: relative;
  cursor: pointer;
  text-align: center;
}

.user-type-option input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
}

.user-type-label {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px 8px;
  border-radius: 14px;
  font-size: 11px;
  font-weight: 700;
  color: #718096;
  transition: all 0.2s ease;
  cursor: pointer;
  border: 2px solid transparent;
  text-align: center;
  word-wrap: break-word;
}

.user-type-label:hover {
  background: rgba(255,255,255,0.8);
  transform: translateY(-2px);
}

.user-type-emoji {
  font-size: 28px;
  display: block;
  line-height: 1;
}

.user-type-label > span:last-child {
  display: block;
  width: 100%;
}

.user-type-option input:checked + .user-type-label {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
  border-color: #667eea;
}

.form-container {
  margin-top: 20px;
}

.input-group {
  margin-bottom: 10px;
}

.input-label {
  font-size: 13px;
  font-weight: 700;
  color: #4a5568;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.input-field {
  width: 100%;
  padding: 8px 18px;
  border: 2px solid #e2e8f0;
  border-radius: 14px;
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  transition: all 0.2s ease;
  outline: none;
  box-sizing: border-box;
  background: white;
}

.input-field:focus {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 4px rgba(139, 92, 246, 0.15);
}

.input-field::placeholder {
  color: #a0aec0;
  font-weight: 500;
}

.input-field:disabled {
  background: #f7fafc;
  color: #718096;
}

.textarea-field {
  width: 100%;
  padding: 14px 18px;
  border: 2px solid #e2e8f0;
  border-radius: 14px;
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  transition: all 0.2s ease;
  outline: none;
  box-sizing: border-box;
  resize: vertical;
  font-family: inherit;
  background: white;
}

.textarea-field:focus {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 4px rgba(139, 92, 246, 0.15);
}

.select-field {
  width: 100%;
  padding: 14px 18px;
  border: 2px solid #e2e8f0;
  border-radius: 14px;
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  transition: all 0.2s ease;
  outline: none;
  box-sizing: border-box;
  cursor: pointer;
  background: white url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%238b5cf6%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E') no-repeat right 16px center / 12px;
  appearance: none;
}

.select-field:focus {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 4px rgba(139, 92, 246, 0.15);
}

.password-wrapper {
  position: relative;
}

.password-toggle {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: #8b5cf6;
  font-size: 16px;
  transition: color 0.2s;
}

.password-toggle:hover {
  color: #6d28d9;
}

.error-text {
  color: #e53e3e;
  font-size: 12px;
  font-weight: 600;
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.checkbox-container {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 10px 0;
  padding: 8px 14px;
  background: linear-gradient(135deg, #f0fff4, #c6f6d5);
  border-radius: 14px;
  border: 2px solid #68d391;
}

.checkbox-input {
  width: 18px;
  height: 18px;
  accent-color: #8b5cf6;
  cursor: pointer;
  flex-shrink: 0;
}

.checkbox-label {
  font-size: 13px;
  color: #22543d;
  cursor: pointer;
  font-weight: 600;
}

.submit-button {
  width: 100%;
  padding: 18px;
  border: none;
  border-radius: 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  font-size: 18px;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 8px 30px rgba(102, 126, 234, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.submit-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 40px rgba(102, 126, 234, 0.5);
}

.submit-button:active {
  transform: translateY(-1px);
}

.links-container {
  text-align: center;
  margin-top: 28px;
  padding-top: 20px;
  border-top: 2px dashed #e2e8f0;
}

.links-text {
  font-size: 14px;
  color: #718096;
  font-weight: 600;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 8px;
}

.nav-link {
  color: #8b5cf6;
  cursor: pointer;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.2s;
  padding: 4px 8px;
  border-radius: 6px;
}

.nav-link:hover {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
}

.divider {
  color: #cbd5e0;
  margin: 0 8px;
}

/* Two column layout for some fields */
.two-column {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
}

/* Mobile Styles - Scrollable */
@media (max-width: 600px) {
  .signup-page-container {
    height: auto;
    min-height: 100vh;
    padding: 20px 15px;
    overflow-y: auto;
    align-items: stretch;
  }

  .signup-layout {
    flex-direction: column;
    align-items: stretch;
    gap: 18px;
  }

  .signup-sidebar,
  .signup-main-column {
    width: 100%;
    max-width: 100%;
  }

  .signup-card {
    border-radius: 24px;
    max-height: none;
    overflow: visible;
  }

  .signup-card-inner {
    padding: 30px 25px;
    overflow-y: visible;
  }

  .signup-title {
    font-size: 28px;
  }

  .user-type-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .user-type-label {
    padding: 10px 6px;
    font-size: 10px;
  }

  .user-type-emoji {
    font-size: 24px;
  }

  .two-column {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 400px) {
  .user-type-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}


@media (max-width: 768px) {
  .signup-page-container {
    flex-direction: column;
  }
}
      `}</style>

      <div className="signup-page-container">
        {/* Decorative floating elements */}
        {/* <div className="decoration decoration-1">🎓</div>
        <div className="decoration decoration-2">📚</div>
        <div className="decoration decoration-3">✏️</div>
        <div className="decoration decoration-4">🎨</div>
        <div className="decoration decoration-5">⭐</div>
        <div className="decoration decoration-6">🌟</div>
        <div className="decoration decoration-7">📝</div>
        <div className="decoration decoration-8">🏫</div> */}

      
          <div className="signup-sidebar">
            <RegistrationStepper activeStep={activeStepIndex} />
          </div>
  <div className="signup-layout">
          <div className="signup-main-column">

        {/* Registration notice - outside the card */}
        <div className="signup-notice">
          <p style={{ margin: 0, fontSize: "17px", fontWeight: 800, color: "#667eea", lineHeight: 1.6, letterSpacing: "0.01em" }}>
            आगे बढ़ने से पहले कृपया अपने आपको रजिस्टर कर लें।
          </p>
          <p style={{ margin: "6px 0 0", fontSize: "15px", fontWeight: 600, color: "#667eea", lineHeight: 1.6 }}>
            रजिस्ट्रेशन ज़रूरी है – मॉड्यूल सेव करने, प्रगति ट्रैक करने और नियमित अपडेट व ऑफ़र पाने के लिए।
          </p>
          <div style={{ width: "40px", height: "2px", background: "linear-gradient(90deg, #667eea, #667eea)", borderRadius: "2px", margin: "10px auto" }} />
          <p style={{ margin: 0, fontSize: "18px", fontWeight: 500, color: "#667eea", lineHeight: 1.6, fontStyle: "italic", letterSpacing: "0.01em" }}>
            Registration is required to save work in modules, track progress &amp; get regular updates &amp; offers.
          </p>
        </div>

        <div className="signup-card">
          <div className="signup-card-inner">
            <div className="signup-header">
              <h1 className="signup-title">
                Create <span className="gradient-text">Account</span>
              </h1>
            </div>

            {loader ? (
              <RegistrationLoader />
            ) : (
              <>
                {/* User Type Selection */}
                <span>Register as:</span>
                <div className="user-type-section">
                  <label className="section-label"></label>
                  <div className="user-type-grid">
                    {userTypes.map((type) => (
                      <div key={type.value} className="user-type-option">
                        <input
                          type="radio"
                          id={type.value}
                          name="userType"
                          value={type.value}
                          checked={userType === type.value}
                          onChange={(e) => {
                            setUserType(e.target.value);
                            resumeAudio();
                          }}
                        />
                        <label htmlFor={type.value} className="user-type-label">
                          <span className="user-type-emoji">{type.emoji}</span>
                          <span>{type.label}</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Conditional Forms */}
                {userType === "student" && <StudentRegistrationForm stopAudio={stopAudio} />}
                {userType === "individual" && <IndividualRegistrationForm stopAudio={stopAudio} />}
                {userType === "other" && <ForOthersRegistrationForm stopAudio={stopAudio} />}

                {userType === "school" && (
                  <motion.form
                    onSubmit={handleSubmit}
                    className="form-container"
                    animate={{ opacity: loading ? 0.4 : 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="input-group">
                      <label className="input-label">🏫 School Name</label>
                      <input
                        type="text"
                        name="schoolName"
                        required
                        placeholder="Enter your school name"
                        value={formData.schoolName}
                        onChange={handleChange}
                        className="input-field"
                      />
                    </div>

                    <div className="two-column">
                      <div className="input-group">
                        <label className="input-label">📋 School Type</label>
                        <select
                          name="schoolType"
                          required
                          value={formData.schoolType}
                          onChange={handleChange}
                          className="select-field"
                        >
                          <option value="">Select type</option>
                          <option value="Preparatory">Preparatory</option>
                          <option value="Primary">Primary</option>
                          <option value="Secondary">Secondary</option>
                          <option value="Senior Secondary">
                            Senior Secondary
                          </option>
                        </select>
                      </div>

                      <div className="input-group">
                        <label className="input-label">
                          💼 Role / Designation
                        </label>
                        <select
                          name="designation"
                          required
                          value={formData.designation}
                          onChange={handleChange}
                          className="select-field"
                        >
                          <option value="" disabled>
                            Select role
                          </option>
                          <option value="Principal">Principal</option>
                          <option value="Vice Principal">Vice Principal</option>
                          <option value="Admin">Admin</option>
                          <option value="Others">Others</option>
                        </select>
                      </div>
                    </div>

                    <div className="input-group">
                      <label className="input-label">👤 Admin Name</label>
                      <input
                        type="text"
                        name="adminName"
                        required
                        placeholder="Enter admin name"
                        value={formData.adminName}
                        onChange={handleChange}
                        className="input-field"
                        disabled={!!adminName}
                      />
                    </div>

                    <div className="input-group">
                      <label className="input-label">📍 Address</label>
                      <textarea
                        name="address"
                        placeholder="Enter school address"
                        required
                        value={formData.address}
                        onChange={handleChange}
                        className="textarea-field"
                        rows="3"
                      />
                    </div>

                    <div className="input-group">
                      <label className="input-label">🏙️ City</label>
                      <input
                        type="text"
                        name="city"
                        required
                        placeholder="Enter city name"
                        value={formData.city}
                        onChange={handleChange}
                        className="input-field"
                      />
                    </div>

                    <div className="two-column">
                      <div className="input-group">
                        <label className="input-label">📧 Email Address</label>
                        <input
                          type="email"
                          name="email"
                          placeholder="Enter email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="input-field"
                          disabled={!!googleEmail}
                        />
                      </div>

                      <div className="input-group">
                        <label className="input-label">📱 Contact Number</label>
                        <input
                          type="tel"
                          name="contactNumber"
                          required
                          placeholder="10 digit number"
                          maxLength={10}
                          value={formData.contactNumber}
                          disabled={!!mobile}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^\d*$/.test(value)) {
                              setFormData({
                                ...formData,
                                contactNumber: value,
                              });
                              if (value.length !== 10) {
                                setPhoneError("Must be 10 digits");
                              } else {
                                setPhoneError("");
                              }
                            }
                          }}
                          className="input-field"
                        />
                        {phoneError && (
                          <span className="error-text">⚠️ {phoneError}</span>
                        )}
                      </div>
                    </div>

                    <div className="two-column">
                      <div className="input-group">
                        <label className="input-label">🔒 Password</label>
                        <div className="password-wrapper">
                          <input
                            type={showPassword ? "text" : "password"}
                            name="password"
                            required
                            placeholder="Create password"
                            value={formData.password}
                            onChange={handleChange}
                            className="input-field"
                          />
                          <i
                            className={`fa-solid ${showPassword ? "fa-eye-slash" : "fa-eye"
                              } password-toggle`}
                            onClick={() => setShowPassword(!showPassword)}
                          ></i>
                        </div>
                      </div>

                      <div className="input-group">
                        <label className="input-label">
                          🔐 Confirm Password
                        </label>
                        <div className="password-wrapper">
                          <input
                            type={showConfirmPassword ? "text" : "password"}
                            name="confirmPassword"
                            required
                            placeholder="Confirm password"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            className="input-field"
                          />
                          <i
                            className={`fa-solid ${showConfirmPassword ? "fa-eye-slash" : "fa-eye"
                              } password-toggle`}
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                          ></i>
                        </div>
                      </div>
                    </div>
                    <div className="input-group">
                      <label className="input-label">
                        👤 Referral Code (Optional)
                      </label>
                      <input
                        type="text"
                        name="referralCode"
                        placeholder="Referral Code (Optional)"
                        value={formData.referralCode}
                        maxLength={8}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^[a-zA-Z0-9]*$/.test(value)) {
                            setFormData({ ...formData, referralCode: value });
                          }
                        }}
                        className="form-control"
                      />
                      {formData.referralCode &&
                        !/^[a-zA-Z0-9]{8}$/.test(formData.referralCode) && (
                          <small style={{ color: "red", fontSize: "12px" }}>
                            Referral code must be exactly 8 alphanumeric
                            characters
                          </small>
                        )}
                    </div>

                    <div className="checkbox-container">
                      <input
                        type="checkbox"
                        id="terms"
                        checked={agreed}
                        required
                        onChange={(e) => {
                          if (e.target.checked) {
                            setShowPopup(true);
                          } else {
                            setAgreed(false);
                          }
                        }}
                        className="checkbox-input"
                      />
                      <label htmlFor="terms" className="checkbox-label">
                        ✅ I agree to the Terms & Conditions
                      </label>
                    </div>

                    {showPopup && (
                      <Popup
                        title="Terms and Conditions"
                        height="100px"
                        maxWidth="1300px"
                        minHeight="100px"
                        onClose={() => setShowPopup(false)}
                      >
                        <TermsAndConditions
                          agreed={agreed}
                          setAgreed={(val) => {
                            setAgreed(val);
                            setShowPopup(false);
                          }}
                        />
                      </Popup>
                    )}

                    <button type="submit" className="submit-button">
                      <span className="btn-emoji">🚀</span>
                      Sign Up Now!
                      <span className="btn-emoji">✨</span>
                    </button>
                  </motion.form>
                )}

                {userType === "coaching" && (
                  <motion.form
                    onSubmit={handleSubmit}
                    className="form-container"
                    animate={{ opacity: loading ? 0.4 : 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="input-group">
                      <label className="input-label">📚 Coaching Name</label>
                      <input
                        type="text"
                        name="schoolName"
                        required
                        placeholder="Enter coaching name"
                        value={formData.schoolName}
                        onChange={handleChange}
                        className="input-field"
                      />
                    </div>

                    <div className="two-column">
                      <div className="input-group">
                        <label className="input-label">📋 Coaching Type</label>
                        <select
                          name="schoolType"
                          required
                          value={formData.schoolType}
                          onChange={handleChange}
                          className="select-field"
                        >
                          <option value="">Select type</option>
                          <option value="school_college_stu">
                            School & College Students
                          </option>
                          <option value="competitive_exams">
                            Competitive Exams
                          </option>
                          <option value="language_training">
                            Language Training Center
                          </option>
                          <option value="Other">Others</option>
                        </select>
                      </div>

                      <div className="input-group">
                        <label className="input-label">
                          💼 Role / Designation
                        </label>
                        <select
                          name="designation"
                          required
                          value={formData.designation}
                          onChange={handleChange}
                          className="select-field"
                        >
                          <option value="" disabled>
                            Select role
                          </option>
                          <option value="Principal">Owner</option>
                          <option value="Management">Management</option>
                          <option value="Teacher">Teacher</option>
                          <option value="Admin">Admin</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    </div>

                    <div className="input-group">
                      <label className="input-label">👤 Name</label>
                      <input
                        type="text"
                        name="adminName"
                        required
                        placeholder="Enter your name"
                        value={formData.adminName}
                        onChange={handleChange}
                        className="input-field"
                        disabled={!!adminName}
                      />
                    </div>

                    <div className="input-group">
                      <label className="input-label">📍 Address</label>
                      <textarea
                        name="address"
                        placeholder="Enter address"
                        required
                        value={formData.address}
                        onChange={handleChange}
                        className="textarea-field"
                        rows="3"
                      />
                    </div>

                    <div className="input-group">
                      <label className="input-label">🏙️ City</label>
                      <input
                        type="text"
                        name="city"
                        required
                        placeholder="Enter city name"
                        value={formData.city}
                        onChange={handleChange}
                        className="input-field"
                      />
                    </div>

                    <div className="two-column">
                      <div className="input-group">
                        <label className="input-label">📧 Email Address</label>
                        <input
                          type="email"
                          name="email"
                          placeholder="Enter email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="input-field"
                          disabled={!!googleEmail}
                        />
                      </div>

                      <div className="input-group">
                        <label className="input-label">📱 Contact Number</label>
                        <input
                          type="tel"
                          name="contactNumber"
                          required
                          placeholder="10 digit number"
                          maxLength={10}
                          value={formData.contactNumber}
                          disabled={!!mobile}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^\d*$/.test(value)) {
                              setFormData({
                                ...formData,
                                contactNumber: value,
                              });
                              if (value.length !== 10) {
                                setPhoneError("Must be 10 digits");
                              } else {
                                setPhoneError("");
                              }
                            }
                          }}
                          className="input-field"
                        />
                        {phoneError && (
                          <span className="error-text">⚠️ {phoneError}</span>
                        )}
                      </div>
                    </div>

                    <div className="two-column">
                      <div className="input-group">
                        <label className="input-label">🔒 Password</label>
                        <div className="password-wrapper">
                          <input
                            type={showPassword ? "text" : "password"}
                            name="password"
                            required
                            placeholder="Create password"
                            value={formData.password}
                            onChange={handleChange}
                            className="input-field"
                          />
                          <i
                            className={`fa-solid ${showPassword ? "fa-eye-slash" : "fa-eye"
                              } password-toggle`}
                            onClick={() => setShowPassword(!showPassword)}
                          ></i>
                        </div>
                      </div>

                      <div className="input-group">
                        <label className="input-label">
                          🔐 Confirm Password
                        </label>
                        <div className="password-wrapper">
                          <input
                            type={showConfirmPassword ? "text" : "password"}
                            name="confirmPassword"
                            required
                            placeholder="Confirm password"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            className="input-field"
                          />
                          <i
                            className={`fa-solid ${showConfirmPassword ? "fa-eye-slash" : "fa-eye"
                              } password-toggle`}
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                          ></i>
                        </div>
                      </div>
                    </div>

                    <div className="input-group">
                      <label className="input-label">
                        👤 Referral Code (Optional)
                      </label>
                      <input
                        type="text"
                        name="referralCode"
                        placeholder="Referral Code (Optional)"
                        value={formData.referralCode}
                        maxLength={8}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^[a-zA-Z0-9]*$/.test(value)) {
                            setFormData({ ...formData, referralCode: value });
                          }
                        }}
                        className="form-control"
                      />
                      {formData.referralCode &&
                        !/^[a-zA-Z0-9]{8}$/.test(formData.referralCode) && (
                          <small style={{ color: "red", fontSize: "12px" }}>
                            Referral code must be exactly 8 alphanumeric
                            characters
                          </small>
                        )}
                    </div>

                    <div className="checkbox-container">
                      <input
                        type="checkbox"
                        id="terms-coaching"
                        checked={agreed}
                        required
                        onChange={(e) => {
                          if (e.target.checked) {
                            setShowPopup(true);
                          } else {
                            setAgreed(false);
                          }
                        }}
                        className="checkbox-input"
                      />
                      <label
                        htmlFor="terms-coaching"
                        className="checkbox-label"
                      >
                        ✅ I agree to the Terms & Conditions
                      </label>
                    </div>

                    {showPopup && (
                      <Popup
                        title="Terms and Conditions"
                        height="100px"
                        maxWidth="1300px"
                        minHeight="100px"
                        onClose={() => setShowPopup(false)}
                      >
                        <TermsAndConditions
                          agreed={agreed}
                          setAgreed={(val) => {
                            setAgreed(val);
                            setShowPopup(false);
                          }}
                        />
                      </Popup>
                    )}

                    <button type="submit" className="submit-button">
                      <span className="btn-emoji">🚀</span>
                      Sign Up Now!
                      <span className="btn-emoji">✨</span>
                    </button>
                  </motion.form>
                )}

                <div className="links-container">
                  <span className="links-text">
                    Already have an account?{" "}
                    <span
                      className="nav-link"
                      onClick={() => { stopAudio(); navigate("/login"); }}
                    >
                      Login
                    </span>
                    <span className="divider">|</span>
                    <span className="nav-link" onClick={() => { stopAudio(); navigate("/"); }}>
                      Home
                    </span>
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignupPage;
